import * as React from 'react';
import { RootStore, withInjectedRootStore } from '../../../../providers';
import { useTranslation } from 'react-i18next';
import { Text, CustomModalLayout } from '@wix/design-system';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Brands, ChangePlanErrorType, Experiments } from 'enums';

interface ChangePlanErrorModalProps extends InjectedExperimentsProps {
  rootStore: RootStore;
  errorType: ChangePlanErrorType;
}

const ChangePlanErrorModalComp: React.FC<ChangePlanErrorModalProps> = ({
  rootStore,
  experiments,
  errorType,
}) => {
  const { t } = useTranslation();
  const { closeChangePlanModal, appContext, isEditorX } = rootStore.appStore;
  const { userPackageGuid } = appContext.currentPlanDetails!;
  const contactSupport = () => {
    window.location.assign(
      'https://www.wix.com/about/contact-us?referralAdditionalInfo=advPlans'
    );
  };
  const goToUpdatePaymentMethod = () => {
    const brand = isEditorX ? Brands.EDITOR_X : Brands.WIX;
    const directUpdatePaymentMethodLink = `https://manage.${brand}.com/account/subscriptions?premiumId=${userPackageGuid}&invalidEuVat=true`;
    window.open(directUpdatePaymentMethodLink, '_blank');
    closeChangePlanModal();
  };
  const isErrorMessagesImprovementEnabled = experiments.enabled(
    Experiments.ErrorMessagesImprovement
  );
  const { INVALID_EU_VAT, DATA_RETRIEVAL_FAILED } = ChangePlanErrorType;
  const isDataRetrievalError = errorType === DATA_RETRIEVAL_FAILED;

  const modalButtonsProps = () => {
    if (isErrorMessagesImprovementEnabled && !isDataRetrievalError) {
      return {
        primaryButtonText: t(
          'Advanced.Plans_change.plan_modal_error_support_contact'
        ),
        primaryButtonOnClick: contactSupport,
        secondaryButtonText: t('change_plan.change_plan_modal.ok'),
        secondaryButtonOnClick: closeChangePlanModal,
      };
    }

    if (errorType === INVALID_EU_VAT) {
      return {
        primaryButtonText: t(
          'Advanced_Plans_change_plan_modal_eu_vat_error_upm_button'
        ),
        primaryButtonOnClick: goToUpdatePaymentMethod,
        secondaryButtonText: t('change_plan.change_plan_modal.ok'),
        secondaryButtonOnClick: closeChangePlanModal,
      };
    }

    return {
      primaryButtonText: t('change_plan.change_plan_modal.ok'),
      primaryButtonOnClick: closeChangePlanModal,
      secondaryButtonText: t(
        'Advanced.Plans_change.plan_modal_error_support_contact'
      ),
      secondaryButtonOnClick: contactSupport,
    };
  };

  const titleKey: string =
    isErrorMessagesImprovementEnabled && isDataRetrievalError
      ? 'Advanced_Plans_change_plan_modal_data_retrieval_error_title'
      : 'Advanced.Plans_change.plan_modal_error.title';

  const errorBodyKeys = (): string[] => {
    if (isErrorMessagesImprovementEnabled && isDataRetrievalError) {
      return ['Advanced_Plans_change_plan_modal_data_retrieval_error_body'];
    }
    if (errorType === INVALID_EU_VAT) {
      return [
        'Advanced_Plans_change_plan_modal_eu_vat_error_body_par1',
        '\n\n',
        'Advanced_Plans_change_plan_modal_eu_vat_error_body_par2',
      ];
    }
    return ['Advanced.Plans_change.plan_modal_error.body'];
  };

  return (
    <CustomModalLayout
      width="604px"
      title={t(titleKey)}
      dataHook="change_plan_error_modal"
      onCloseButtonClick={closeChangePlanModal}
      {...modalButtonsProps()}
    >
      <Text data-testid="change_plan_error" dataHook="change-plan-error">
        {errorBodyKeys().map(t)}
      </Text>
    </CustomModalLayout>
  );
};

export const ChangePlanErrorModal = withInjectedRootStore(
  withExperiments(ChangePlanErrorModalComp)
);
